<template>
  <el-dialog
    title="订单修改记录"
    :visible="visible"
    :close-on-click-modal="false"
    width="760px"
    top="8vh"
    custom-class="ocl-dialog"
    @update:visible="handleVisibleChange"
    @closed="handleClosed"
  >
    <div v-loading="loading" class="ocl-body">
      <!-- 订单摘要 -->
      <div v-if="hasSummary" class="ocl-summary">
        <div v-if="summary.systemOrderNo" class="ocl-summary__row">
          <span class="ocl-summary__label">系统订单号</span>
          <span class="ocl-summary__val">{{ summary.systemOrderNo }}</span>
        </div>
        <div v-if="summary.hotel" class="ocl-summary__row">
          <span class="ocl-summary__label">酒店</span>
          <span class="ocl-summary__val">{{ summary.hotel }}</span>
        </div>
        <div v-if="summary.roomType" class="ocl-summary__row">
          <span class="ocl-summary__label">房型</span>
          <span class="ocl-summary__val">{{ summary.roomType }}</span>
        </div>
      </div>

      <!-- 修改记录（最新在上） -->
      <el-divider content-position="left" class="ocl-divider">
        修改记录<span class="ocl-muted">（最新在上）</span>
      </el-divider>

      <div v-if="logs.length" class="ocl-timeline-wrap">
        <el-timeline class="ocl-timeline">
          <el-timeline-item
            v-for="(log, i) in logs"
            :key="log.id || i"
            :timestamp="log.created_at || ''"
            placement="top"
            :color="dotColor(log)"
          >
            <div class="ocl-head">
              <el-tag
                size="small"
                :type="tagType(log)"
                effect="plain"
                class="ocl-actor"
              >
                {{ actorText(log) }}
              </el-tag>
              <span class="ocl-action">{{ actionText(log) }}</span>
            </div>

            <div v-if="fieldList(log).length" class="ocl-fields">
              <div
                v-for="(det, j) in fieldList(log)"
                :key="j"
                class="ocl-field"
              >
                <span class="ocl-field__name">{{ fieldName(det.field) }}</span>
                <span class="ocl-field__vals">
                  <span v-if="hasVal(det.old_value)" class="ocl-old">
                    {{ valueText(det, 'old') }}
                  </span>
                  <span v-if="hasVal(det.old_value)" class="ocl-arrow">
                    →
                  </span>
                  <span class="ocl-new">{{ valueText(det, 'new') }}</span>
                </span>
              </div>
            </div>
          </el-timeline-item>
        </el-timeline>
      </div>

      <div v-else-if="!loading" class="ocl-empty">暂无修改记录</div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleVisibleChange(false)">
        关闭
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import request from '@/utils/request'

/**
 * 字段名 → 中文标签
 * 全部取自本工程已上线的写法（orderList.vue 搜索栏 placeholder、OrderChangeLog.vue 表头、
 * dida/orderMonitor.vue 详情 label），不自造业务文案。
 */
const FIELD_LABELS = {
  order_state: '供应商订单状态',
  system_order_status: '系统订单状态',
  system_order_cancel_status: '系统订单取消状态',
  pay_state: '供应商支付状态',
  quota_status: '额度状态',
  quota_value: '已扣额度',
  manual_override_status: '管理员手动修改状态',
  error_order_handle_status: '异常处理',
  order_code: '供应商订单号',
  order_confirm_no: '确认号',
  refund_message: '退款说明',
  actual_channel_money: '合作商渠道价',
  actual_total_rate: '供应商成本价',
  actual_end_travel_date: '实际离店时间',
  app_client_account_log_no: '账户流水号',
  revoke_app_client_account_log_no: '回退账户流水号', // [待确认：工程内未见既有中文写法]
}

/**
 * 值域 → 中文（兜底）
 * 接口多数情况下已下发 old_value_label / new_value_label（优先使用）；
 * 这里仅在未下发时按本工程既有值域字典转换。
 * 来源：orderList.vue 搜索栏 el-option、dida/orderMonitor.vue quotaStatusName / cancelStatusName
 */
const VALUE_LABELS = {
  order_state: {
    1: '预订成功',
    2: '已取消',
    3: '预订未到',
    4: '已入住',
    5: '已完成',
  },
  system_order_status: {
    100: '下单待处理',
    101: '下单成功',
    102: '下单失败',
    103: '下单确认中',
    201: '取消订单成功',
    202: '取消订单失败',
    203: '取消订单中',
  },
  system_order_cancel_status: {
    0: '未取消',
    1: '已取消',
    2: '取消失败',
    3: '取消中',
  },
  pay_state: {
    0: '未付款',
    1: '已付款',
    2: '退款处理中',
    3: '已退款',
  },
  quota_status: {
    '-1': '已退额度',
    0: '未付额度',
    1: '已付额度',
    2: '部分退额度',
  },
  manual_override_status: {
    0: '未手动修改',
    1: '已手动修改',
    2: '生成台账不可更新',
  },
  error_order_handle_status: {
    0: '未处理',
    1: '已处理',
  },
}

// 修改类型选项（模块级缓存，避免每次打开弹窗重复请求）
let changeTypeOptionCache = null

/**
 * 操作来源系统 → 显示前缀
 * 总台后台的人工操作显示「管理员（姓名）」；客服后台的人工操作显示「客服（姓名）」。
 * ⚠️ 依赖后端在 fieldLogList 每条记录下发来源系统标识：
 *    建议字段 operator_source（'admin' = 总台后台 / 'service' = 客服后台），
 *    并下发操作人姓名 operator_name（如「林子杰」）。
 *    后端未下发时，本组件自动降级为原有展示（账号名 / 操作者类型），不会显示错误信息。
 */
const SOURCE_PREFIX = {
  admin: '管理员', // 总台后台
  service: '客服', // 客服后台
}

export default {
  name: 'OrderChangeLogDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    // 订单列表行（取 order_id 作为查询条件）
    orderInfo: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      logs: [],
      changeTypeMap: Object.assign({}, changeTypeOptionCache || {}),
    }
  },
  computed: {
    // 查询用的订单号：fieldLogList 的 order_id 对应订单列表行的 order_id
    orderId() {
      const r = this.orderInfo
      if (!r) return ''
      return r.order_id || r.id || ''
    },
    hasSummary() {
      const s = this.summary
      return !!(s.systemOrderNo || s.hotel || s.roomType)
    },
    summary() {
      const r = this.orderInfo || {}
      const hotel = r.hotel
      const roomType = r.room_type
      return {
        systemOrderNo: r.system_order_no || r.order_id || '',
        hotel:
          (hotel && (hotel.inn_name || hotel.name)) ||
          r.inn_name ||
          r.hotel_name ||
          '',
        roomType:
          (roomType && (roomType.room_type_name || roomType.name)) ||
          r.room_type_name ||
          '',
      }
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadChangeTypes()
        this.fetchLogs()
      }
    },
  },
  created() {
    if (this.visible) {
      this.loadChangeTypes()
      this.fetchLogs()
    }
  },
  methods: {
    handleVisibleChange(v) {
      this.$emit('update:visible', v)
    },
    handleClosed() {
      this.logs = []
    },
    // 拉取修改类型选项（用于 change_type → 中文）
    loadChangeTypes() {
      if (changeTypeOptionCache) {
        this.changeTypeMap = Object.assign({}, changeTypeOptionCache)
        return Promise.resolve()
      }
      return request({
        url: '/api/backend/order/changeTypeOptions',
        method: 'get',
      })
        .then((res) => {
          const opts = (res.data && res.data.change_type_options) || []
          const map = {}
          opts.forEach((o) => {
            map[o.value] = o.label
          })
          changeTypeOptionCache = map
          this.changeTypeMap = Object.assign({}, map)
        })
        .catch(() => {})
    },
    fetchLogs() {
      const orderId = this.orderId
      if (!orderId) {
        this.logs = []
        return
      }
      this.loading = true
      request({
        url: '/api/backend/order/fieldLogList',
        method: 'get',
        params: {
          order_id: orderId,
          page: 1,
          limit: 500,
        },
      })
        .then((response) => {
          const payload = (response.data && response.data.data) || []
          // ⚠️ 接口的 order_id 为「模糊匹配」（如 order_id=5 会返回所有含 "5" 的记录），
          //    因此此处必须按订单号做精确过滤，避免混入其他订单的修改记录。
          const target = String(orderId)
          this.logs = payload
            .filter((r) => String(r.order_id || '') === target)
            .sort((a, b) =>
              String(b.created_at || '').localeCompare(
                String(a.created_at || ''),
              ),
            )
        })
        .catch(() => {
          this.logs = []
          this.$message.error('获取订单修改记录失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    fieldList(log) {
      return (log && log.change_details) || []
    },
    /**
     * 操作者显示名
     * 人工操作按来源系统区分并带姓名：
     *   operator_source = 'admin'   → 管理员（姓名）   【总台后台】
     *   operator_source = 'service' → 客服（姓名）     【客服后台】
     * 兼容别名：operator_from / source_system；姓名字段：operator_name / operator_real_name / operator_nickname
     * 后端未下发来源时降级为原有展示（账号名 / 操作者类型 / 产生来源）。
     */
    actorText(log) {
      if (!log) return '系统'
      const src = String(
        log.operator_source || log.operator_from || log.source_system || '',
      ).toLowerCase()
      const name =
        log.operator_name ||
        log.operator_real_name ||
        log.operator_nickname ||
        log.operator_username ||
        ''
      const prefix = SOURCE_PREFIX[src]
      if (prefix) {
        if (!name) return prefix
        // 姓名本身已含该前缀（如后端下发「客服超级管理员」）→ 直接显示，避免「客服（客服超级管理员）」
        if (name.indexOf(prefix) === 0) return name
        return prefix + '（' + name + '）'
      }
      // 降级：沿用原有展示
      return (
        log.operator_username ||
        log.operator_type_label ||
        log.source_label ||
        '系统'
      )
    },
    // 人工（总台管理员 / 客服后台客服）为蓝色 primary，系统 / API 为灰色 info（对齐工单详情操作日志）
    isManual(log) {
      if (!log) return false
      // 后端下发了来源系统 ⇒ 该条为人工操作
      const src = String(
        log.operator_source || log.operator_from || log.source_system || '',
      ).toLowerCase()
      if (SOURCE_PREFIX[src]) return true
      return Number(log.operator_type) === 1
    },
    tagType(log) {
      return this.isManual(log) ? 'primary' : 'info'
    },
    dotColor(log) {
      return this.isManual(log) ? '#409EFF' : '#909399'
    },
    // 主文案：修改类型中文，回退到修改大类中文
    actionText(log) {
      return (
        this.changeTypeMap[log.change_type] ||
        log.change_type_label ||
        log.change_category_label ||
        log.change_type ||
        '-'
      )
    },
    fieldName(field) {
      if (!field) return '-'
      return FIELD_LABELS[field] || field
    },
    hasVal(v) {
      return v !== null && v !== undefined && v !== ''
    },
    // 优先使用后端下发的中文标签；未下发时按工程既有值域字典兜底；空值显示 '-'
    valueText(det, kind) {
      const labelKey = kind === 'old' ? 'old_value_label' : 'new_value_label'
      const valueKey = kind === 'old' ? 'old_value' : 'new_value'
      const label = det[labelKey]
      if (label !== null && label !== undefined && label !== '') return label
      const v = det[valueKey]
      if (v === null || v === undefined || v === '') return '-'
      const dict = VALUE_LABELS[det.field]
      if (dict && dict[String(v)] !== undefined) return dict[String(v)]
      if (typeof v === 'boolean') return v ? '是' : '否'
      return String(v)
    },
  },
}
</script>

<style lang="scss" scoped>
.ocl-body {
  min-height: 120px;
}

.ocl-summary {
  padding: 10px 12px;
  margin-bottom: 4px;
  background: #f7f9fc;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.8;

  &__row {
    display: flex;
  }

  &__label {
    flex-shrink: 0;
    width: 76px;
    color: #8a94a6;
  }

  &__val {
    color: #1f2733;
    word-break: break-all;
  }
}

.ocl-divider {
  margin: 16px 0 12px;

  ::v-deep .el-divider__text {
    background: #fff;
    color: #303133;
    font-size: 13px;
    font-weight: 600;
  }
}

.ocl-muted {
  margin-left: 4px;
  color: #909399;
  font-size: 12px;
  font-weight: 400;
}

.ocl-timeline-wrap {
  max-height: 52vh;
  overflow-y: auto;
  padding-right: 6px;
}

.ocl-timeline {
  padding-left: 2px;
  margin-bottom: 0;

  ::v-deep .el-timeline-item__timestamp {
    color: #8a94a6;
    font-size: 12px;
  }
}

.ocl-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.ocl-actor {
  margin-right: 6px;
}

.ocl-action {
  color: #1f2733;
  font-size: 13px;
  font-weight: 600;
}

.ocl-fields {
  margin-top: 6px;
  padding: 8px 10px;
  background: #fafbfd;
  border-radius: 6px;
}

.ocl-field {
  display: flex;
  align-items: baseline;
  font-size: 12px;
  line-height: 1.9;

  &__name {
    flex-shrink: 0;
    min-width: 96px;
    color: #8a94a6;
  }

  &__vals {
    word-break: break-all;
  }
}

.ocl-old {
  color: #f56c6c;
  text-decoration: line-through;
}

.ocl-arrow {
  margin: 0 5px;
  color: #c0c4cc;
}

.ocl-new {
  color: #67c23a;
  font-weight: 600;
}

.ocl-empty {
  padding: 36px 0;
  color: #909399;
  font-size: 13px;
  text-align: center;
}
</style>

<style lang="scss">
/* 移动端：弹窗改为接近满屏宽
   注意：工程全局样式给 .el-dialog 设了 min-width:600px（src/styles/index.scss），
   窄屏必须一并覆盖 min-width，否则宽度会被撑到 600px 而溢出 */
@media (max-width: 768px) {
  .ocl-dialog {
    width: 92% !important;
    min-width: auto !important;
    max-width: 92% !important;
    margin-top: 6vh !important;
  }

  .ocl-dialog .ocl-field__name {
    min-width: 0;
    display: block;
  }

  .ocl-dialog .ocl-timeline-wrap {
    max-height: 60vh;
  }
}
</style>
